import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, Tag, Row, Col, Statistic, message, Space, DatePicker, Tabs, Alert, Divider } from 'antd';
import { DollarOutlined, PlusOutlined, CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined, CalculatorOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from 'services/api/apiService';

const typeColors: Record<string, string> = { paye: 'blue', pension: 'green', nhis: 'cyan', itf: 'purple', nsitf: 'orange', housing_fund: 'red' };
const typeLabels: Record<string, string> = { paye: 'PAYE Tax', pension: 'Pension', nhis: 'NHIS', itf: 'ITF', nsitf: 'NSITF', housing_fund: 'Housing Fund' };
const statusColors: Record<string, string> = { pending: 'warning', scheduled: 'processing', paid: 'success', overdue: 'error' };
const fmt = (v: any) => `₦${parseFloat(v||0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;

const StatutoryRemittance: React.FC = () => {
  const [remittances, setRemittances] = useState<any[]>([]);
  const [schedule, setSchedule] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [period, setPeriod] = useState(dayjs().format('YYYY-MM'));
  const [createForm] = Form.useForm();

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [rr, sr] = await Promise.all([
        apiService.getStatutoryRemittances(),
        apiService.getStatutorySummary(),
      ]);
      if (rr.success) setRemittances(rr.data || []);
      if (sr.success) setSummary(sr.data);
    } finally { setLoading(false); }
  };

  const generateSchedule = async () => {
    setGenLoading(true);
    try {
      const r = await apiService.generateRemittanceSchedule(period);
      if (r.success) { setSchedule(r.data || []); message.success(`Schedule generated from ${period} payroll data`); }
      else message.error(r.error || 'Failed — ensure payroll exists for this period');
    } finally { setGenLoading(false); }
  };

  const handleMarkPaid = async (record: any) => {
    const r = await apiService.updateRemittance({ id: record.id, status: 'paid', payment_reference: `REF${Date.now()}` });
    if (r.success) { message.success('Marked as paid'); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const handleCreate = async (values: any) => {
    const r = await apiService.createRemittance(values);
    if (r.success) { message.success(`Remittance ${r.remittance_id} created`); setCreateModal(false); createForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const handleSaveSchedule = async (item: any) => {
    const r = await apiService.createRemittance({
      remittance_type: item.type,
      period_month: period,
      total_amount: item.amount,
      agency_name: item.agency,
      due_date: item.due,
      notes: item.description,
      status: 'pending',
    });
    if (r.success) { message.success('Saved to remittances'); fetchAll(); }
    else message.error(r.error || 'Already exists for this period');
  };

  const tabItems = [
    {
      key: 'remittances',
      label: '📋 All Remittances',
      children: (
        <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModal(true)}>Add Remittance</Button>}>
          <Table dataSource={remittances} rowKey="id" loading={loading}
            columns={[
              { title: 'ID', dataIndex: 'remittance_id', render: (v: string) => <code style={{ fontSize: 11 }}>{v}</code>, width: 130 },
              { title: 'Type', dataIndex: 'remittance_type', render: (v: string) => <Tag color={typeColors[v]}>{typeLabels[v]}</Tag>, width: 100 },
              { title: 'Period', dataIndex: 'period_month', width: 90 },
              { title: 'Agency', dataIndex: 'agency_name' },
              { title: 'Total Amount', dataIndex: 'total_amount', render: fmt, align: 'right' as const },
              { title: 'Due Date', dataIndex: 'due_date', render: (v: string) => { const overdue = v && dayjs(v).isBefore(dayjs()) && true; return <span style={{ color: overdue ? '#ff4d4f' : 'inherit' }}>{v || '-'}</span>; }},
              { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={statusColors[v]}>{v?.toUpperCase()}</Tag>, width: 100 },
              { title: 'Actions', render: (_: any, r: any) => r.status !== 'paid' && <Button size="small" type="primary" icon={<CheckCircleOutlined />} onClick={() => handleMarkPaid(r)}>Mark Paid</Button>, width: 120 },
            ]} />
        </Card>
      )
    },
    {
      key: 'schedule',
      label: '🗓️ Generate Schedule',
      children: (
        <Card title="Auto-Generate from Payroll">
          <Alert message="This generates a statutory remittance schedule based on payroll data for the selected period." type="info" showIcon style={{ marginBottom: 16 }} />
          <Space style={{ marginBottom: 16 }}>
            <DatePicker picker="month" value={dayjs(period)} onChange={d => setPeriod(d ? d.format('YYYY-MM') : dayjs().format('YYYY-MM'))} />
            <Button type="primary" icon={<CalculatorOutlined />} loading={genLoading} onClick={generateSchedule}>Generate Schedule</Button>
          </Space>
          {schedule.length > 0 && (
            <Table dataSource={schedule} rowKey="type" pagination={false}
              columns={[
                { title: 'Type', dataIndex: 'type', render: (v: string) => <Tag color={typeColors[v]}>{typeLabels[v] || v}</Tag> },
                { title: 'Description', dataIndex: 'description' },
                { title: 'Agency', dataIndex: 'agency' },
                { title: 'Amount', dataIndex: 'amount', render: fmt, align: 'right' as const },
                { title: 'Due Date', dataIndex: 'due' },
                { title: '', render: (_: any, r: any) => <Button size="small" onClick={() => handleSaveSchedule(r)}>Save</Button> },
              ]} />
          )}
        </Card>
      )
    },
    {
      key: 'summary',
      label: '📊 Summary',
      children: (
        summary ? (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}><Card><Statistic title="Total Remitted" value={summary.summary?.total_remitted || 0} prefix="₦" formatter={(v: any) => Number(v).toLocaleString()} /></Card></Col>
              <Col span={6}><Card><Statistic title="Total Paid" value={summary.summary?.total_paid || 0} prefix="₦" valueStyle={{ color: '#52c41a' }} formatter={(v: any) => Number(v).toLocaleString()} /></Card></Col>
              <Col span={6}><Card><Statistic title="Pending" value={summary.summary?.total_pending || 0} prefix="₦" valueStyle={{ color: '#faad14' }} formatter={(v: any) => Number(v).toLocaleString()} /></Card></Col>
              <Col span={6}><Card><Statistic title="Overdue" value={summary.summary?.overdue_count || 0} valueStyle={{ color: '#ff4d4f' }} prefix={<ExclamationCircleOutlined />} /></Card></Col>
            </Row>
            <Card title="By Remittance Type">
              <Table dataSource={summary.by_type || []} rowKey="remittance_type" pagination={false}
                columns={[
                  { title: 'Type', dataIndex: 'remittance_type', render: (v: string) => <Tag color={typeColors[v]}>{typeLabels[v]}</Tag> },
                  { title: 'Total Records', dataIndex: 'total_count' },
                  { title: 'Paid', dataIndex: 'paid_count' },
                  { title: 'Total Amount', dataIndex: 'total', render: fmt, align: 'right' as const },
                ]} />
            </Card>
          </div>
        ) : <div>No summary data</div>
      )
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h1>Statutory Remittances</h1>
      <p style={{ color: '#666' }}>Track and manage PAYE, Pension, NHIS, ITF and other statutory obligations</p>
      <Tabs items={tabItems} />

      <Modal title="Add Remittance Record" open={createModal} onCancel={() => { setCreateModal(false); createForm.resetFields(); }} footer={null} width={560}>
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="remittance_type" label="Type" rules={[{ required: true }]}><Select>{Object.entries(typeLabels).map(([k,v]) => <Select.Option key={k} value={k}><Tag color={typeColors[k]}>{v}</Tag></Select.Option>)}</Select></Form.Item></Col>
            <Col span={12}><Form.Item name="period_month" label="Period (YYYY-MM)" rules={[{ required: true }]}><Input placeholder="2026-06" /></Form.Item></Col>
          </Row>
          <Form.Item name="agency_name" label="Agency / Authority" rules={[{ required: true }]}><Input placeholder="e.g. FIRS, Stanbic IBTC PFA" /></Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="total_amount" label="Total Amount (₦)" rules={[{ required: true }]}><Input type="number" /></Form.Item></Col>
            <Col span={12}><Form.Item name="due_date" label="Due Date"><Input type="date" /></Form.Item></Col>
          </Row>
          <Form.Item name="status" label="Status" initialValue="pending"><Select><Select.Option value="pending">Pending</Select.Option><Select.Option value="scheduled">Scheduled</Select.Option><Select.Option value="paid">Paid</Select.Option></Select></Form.Item>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <Space><Button type="primary" htmlType="submit">Save</Button><Button onClick={() => setCreateModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>
    </div>
  );
};

export default StatutoryRemittance;
